'use client';

import { useEffect, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import type { CharacterKey } from '@/lib/voicePreset/types';
import type { VoicePreset } from '@/lib/voicePreset/types';

export function useCharacterVoicePreset(
  charKey: CharacterKey | null,
): { preset: VoicePreset | null; loading: boolean } {
  const voicePresets = useAppStore((s) => s.voicePresets);
  const voicePresetsLoaded = useAppStore((s) => s.voicePresetsLoaded);
  const refreshVoicePresets = useAppStore((s) => s.refreshVoicePresets);
  const [presetId, setPresetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!voicePresetsLoaded) refreshVoicePresets();
  }, [voicePresetsLoaded, refreshVoicePresets]);

  useEffect(() => {
    let cancelled = false;
    if (!charKey) { setPresetId(null); return; }
    setLoading(true);
    (async () => {
      try {
        if (charKey.kind === 'db') {
          // DB characters no longer exist — all chars were migrated to SS projects.
          // This branch is dead code kept to satisfy the CharacterKey union type.
          console.warn('useCharacterVoicePreset: received kind=db key; DB chars are no longer supported.', charKey);
          if (!cancelled) setPresetId(null);
        } else {
          const unifiedId = `ss:${charKey.slug}:${charKey.kebab}`;
          const res = await fetch(
            `/api/short-story-character-extension?id=${encodeURIComponent(unifiedId)}`,
          );
          if (!res.ok) throw new Error('ss ext fetch failed');
          const data = await res.json();
          if (!cancelled) setPresetId(data.extension?.voicePresetId ?? null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setPresetId(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [charKey]);

  const preset = presetId ? voicePresets.find((p) => p.id === presetId) ?? null : null;
  return { preset, loading };
}
